import React from 'react';

import type { FormattedString } from '../types';
import {
  buttonClassName,
  ButtonStyling,
  DEFAULT_VARIANT,
  DEFAULT_SIZE,
} from './styling';
import { ChevronDownIcon } from '../../icons/react';
import { RCTooltip } from '../';

/* eslint-disable react/require-default-props */

export interface RCButtonProps extends ButtonStyling {
  /** The button text is defined via the component's `children` to allow for rich text formatting if necessary,
   * but typically a button should be a simple 1-3 word sentence cased string (e.g. "Add to cart").
   *
   * Our buttons __do not wrap__ by default; keep that in mind when designing for
   * non-EN languages or small areas. If you need to wrap button text, use the `word-wrap-break-word` CSS utility class.
  */
  children: FormattedString;

  /** The click handler passed to the button. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;

  /**
   * Fades the button visually and applies the `disabled` attribute. Keep in mind that if a button is disabled
   * without a way for the user to enable it, it may make more sense to simply not render the button.
   *
   * Either way, if a the button is disabled, an `RCTooltip` explaining _why_ is often a good UX add.
   */
  disabled?: boolean;

  /** Appends `type="submit"` to the button for use inside forms. */
  isSubmit?: boolean;

  /** Passes through to the HTML tag. Should be used when wrapping an icon or something without visible text. */
  title?: string;

  /** Passes through to the HTML tag. Useful for when ARIA attributes need to refer to a button's ID. */
  id?: string;

  /** Passes through to the HTML tag. */
  'aria-disabled'?: boolean;

  /** Passes through to the HTML tag. */
  form?: string;

  /** Cadence SVG icon, like `<ArrowRightIcon />`, or a stateful dropdown indicator.
   * The dropdown indicator's state is controlled by `aria-expanded={true | false}`.
   */
  icon?: 'dropdown' | RCButtonIconProps['icon'];

  /** Hides an iconOnly button's tooltip.
   * Should only be used in rare cases where an icon's meaning is so clear that the label becomes unhelpful.
   */
  hideTooltip?: boolean;

  /** An alternate label for iconOnly buttons. Use the `children` prop as the default. */
  iconLabel?: string;
}

/**
 * It's a button! RCButton renders a `<button>` tag and should be used for button actions whenever possible.
 *
 * A future version of button components will render more tag types; if you need an `<input type="submit">` or `<a>` tag, use our `button` styles.
 */
export const RCButton = React.forwardRef<HTMLButtonElement, RCButtonProps>(
  function RCButton({
    variant = DEFAULT_VARIANT,
    size = DEFAULT_SIZE,
    fullWidth,
    children,
    isSubmit,
    onClick,
    disabled,
    title,
    id,
    icon: iconProp = undefined,
    iconPosition: positionProp = 'before',
    hideTooltip = false,
    iconLabel = undefined,
    ...passthroughProps
  }: RCButtonProps, ref) {
    const icon = iconProp === 'dropdown' ? undefined : iconProp;
    const position = icon ? positionProp : undefined;
    const iconOnly = !!icon && position === 'iconOnly';
    const iconDropdown = iconProp === 'dropdown';
    const iconBefore = !!icon && position === 'before';
    const iconAfter = !!icon && position === 'after';

    function renderIconBefore() {
      if (!iconBefore) return null;
      return <RCButtonIcon icon={icon} />;
    }

    function renderIconAfter() {
      if (iconDropdown) {
        return (
          <span className="rc-button__caret">
            <ChevronDownIcon />
          </span>
        );
      }
      if (!iconAfter) return null;
      return <RCButtonIcon icon={icon} />;
    }

    function renderButton() {
      return (
        <button
          className={buttonClassName({
            variant: variant,
            size: size,
            fullWidth: fullWidth,
            iconPosition: iconDropdown ? 'after' : position,
          })}
          type={isSubmit ? 'submit' : 'button'}
          onClick={onClick}
          disabled={disabled}
          title={title}
          id={id}
          ref={ref}
          {...passthroughProps}
        >
          {iconOnly ? (
            <>
              <RCButtonIcon icon={icon} />
              <span className="visually-hidden">
                {iconLabel || children}
              </span>
            </>
          ) : (
            <>
              {renderIconBefore()}
              {children}
              {renderIconAfter()}
            </>
          )}
        </button>
      );
    }

    if (iconOnly && !hideTooltip) {
      return (
        <RCTooltip
          text={iconLabel || children}
          style="buttonLabel"
          placement="bottom"
          visualOnly
        >
          {renderButton()}
        </RCTooltip>
      );
    }

    return renderButton();
  },
);

interface RCButtonIconProps {
  icon: JSX.Element;
}

export function RCButtonIcon({
  icon,
}: RCButtonIconProps) {
  return (
    <span className="rc-button-icon">
      {icon}
    </span>
  );
}

// Explicitly setting displayName to help with debugging and tracing forwardRef.
RCButton.displayName = 'RCButton';
